<template>
    <div class="tw:block tw:relative tw:w-full" style="display: block; position: relative">
        <e-loading :is-full-page="false" :active="isLoading" />
        <div v-if="slides && Array.isArray(slides) && slides.length > 0">
            <fullscreen ref="fullscreen" class="wrapper" style="position: relative" @change="fullscreenChange">
                <vueper-slides
                    v-if="slides.length > 1"
                    :key="reRender"
                    ref="vueperslides1"
                    touchable
                    disable-arrows-on-edges
                    fade
                    :slide-ratio="slideRatio"
                    :autoplay="false"
                    :bullets="false"
                    arrows
                    @slide="slideAction($event)"
                >
                    <vueper-slide v-for="(slide, i) in slides" :key="i" :image="slide" class="slide" :class="{ 'fullscreen-slide': fullscreen }" />
                </vueper-slides>
                <vueper-slides
                    v-if="slides.length > 1"
                    v-show="!fullscreen || fullScreenNavigation"
                    ref="vueperslides2"
                    class="no-shadow thumbnails"
                    :class="{
                        'thumbnails-two-thirds': slides.length < 8 && slides.length > 4,
                        'thumbnails-half': slides.length <= 4,
                        'thumbnails-one-thirds': slides.length < 3,
                    }"
                    :visible-slides="thumbnailDivisor || 8"
                    :slide-ratio="thumbnailSlideRatio"
                    :bullets="false"
                    :touchable="false"
                    :gap="0.75"
                    :arrows="false"
                    progress
                    @slide="thumbnailAction($event)"
                >
                    <vueper-slide
                        v-for="(slide, i) in slides"
                        :key="i"
                        :image="slide"
                        @click="$refs.vueperslides2.goToSlide(i)"
                    />
                </vueper-slides>

                <e-button class="fullscreen-icon" @click="toggle">
                    <e-icon :icon="fullscreen ? 'compress' : 'expand-arrows-alt'" />
                </e-button>

                <e-button
                    v-show="fullscreen === true && slides.length > 1"
                    :type="fullScreenNavigation ? 'is-primary' : ''"
                    class="fullscreen-icon fullscreen-icon-navigation"
                    style="position: absolute"
                    @click="toggleNavigation"
                >
                    {{ fullScreenNavigation ? 'Hide Slides' : 'Show Slides' }}
                </e-button>

                <img
                    v-if="slides.length === 1"
                    width="100%"
                    :src="slideDeck?.thumbnail_signed_url || slides[0]"
                    :alt="slideDeck?.title || 'Slide'"
                />
            </fullscreen>
        </div>
    </div>
</template>

<script>
import Cookies from 'js-cookie';
import { defineComponent } from 'vue';
import { VueperSlide, VueperSlides } from 'vueperslides';
import 'vueperslides/dist/vueperslides.css';

import { initialState, rootDomain } from '@/Helpers.js';

export default defineComponent({
    name: 'SlideDeck',

    components: { VueperSlides, VueperSlide },

    props: {
        slideDeckId: {
            type: [String, Number],
            required: true,
        },
    },
    data: () => ({
        slides: null,
        fullscreen: false,
        thumbnailDivisor: null,
        imageWidth: null,
        imageHeight: null,
        fullScreenNavigation: true,
        reRender: false,
        thumbnailHeight: 0,
        slideDeck: null,
        activeSlideIndex: 0,
        isLoading: true,
    }),

    computed: {
        courseId() {
            return this.$route.params.courseId;
        },

        activityId() {
            return this.$route.params.activityId;
        },

        hasValidAspectRatio() {
            return this.imageWidth && this.imageHeight && this.imageWidth > 0 && this.imageHeight > 0;
        },

        slideRatio() {
            if (!this.hasValidAspectRatio) {
                return 0.75; // Default 4:3 ratio
            }
            return this.imageHeight / this.imageWidth;
        },

        thumbnailSlideRatio() {
            // We use CSS aspect-ratio: 1/1 on the slides for square thumbnails
            // This value is still needed by vueperslides but won't affect layout
            // since we override padding-bottom: 0 in CSS
            return 1;
        },
    },

    watch: {
        slideDeck: {
            handler(newVal) {
                if (newVal && newVal.slide_deck_urls) {
                    this.initializeSlideDeck();
                }
            },
            immediate: true,
        },
    },

    async created() {
        if (this.courseId && this.activityId) {
            this.logEvent(1);
        }

        this.isLoading = true;

        try {
            // Try to get slide deck from store first
            this.slideDeck = this.$store.getters['SlideDecks/get'](this.slideDeckId);

            // If slide deck doesn't exist in store or doesn't have slide_deck_urls (from list view),
            // fetch the full slide deck data with all URLs
            // Note: List view uses SlideDeckListResource which excludes slide_deck_urls for performance
            if (!this.slideDeck || !this.slideDeck.slide_deck_urls) {
                // Get reference_id from the store data (slideDeckId is numeric id from list view)
                // If slideDeckId is already a reference_id (UUID format), use it directly
                const referenceId =
                    this.slideDeck?.reference_id ||
                    (typeof this.slideDeckId === 'string' && this.slideDeckId.includes('-') ? this.slideDeckId : null);

                if (referenceId) {
                    // Fetch full slide deck data with all URLs using fetchOne (expects reference_id)
                    this.slideDeck = await this.$store.dispatch('SlideDecks/fetchOne', referenceId);
                    // Handle case where fetch succeeded but returned null or incomplete data
                    if (!this.slideDeck || !this.slideDeck.slide_deck_urls) {
                        this.isLoading = false;
                    }
                } else {
                    this.isLoading = false;
                }
            } else {
                // Slide deck is already available with slide_deck_urls, no need to fetch
                // The watcher will handle initialization, but we can set loading to false here
                // since initializeSlideDeck will be called by the watcher
            }
        } catch {
            this.isLoading = false;
        }
    },

    beforeUnmount() {
        document.removeEventListener('keyup', this.changeSlide);
    },

    methods: {
        initializeSlideDeck() {
            if (!this.slideDeck) {
                this.isLoading = false;
                return;
            }

            // Only set cookies if they exist (CloudFront resolver)
            if (this.slideDeck.slide_deck_cookies) {
                Object.entries(this.slideDeck.slide_deck_cookies).forEach(([key, value]) => {
                    Cookies.set(key, value, { domain: rootDomain(initialState('AppHost')) });
                });
            }

            // Ensure slide_deck_urls is an array
            // Handle both array and object formats (object with numeric keys)
            if (Array.isArray(this.slideDeck.slide_deck_urls)) {
                this.slides = this.slideDeck.slide_deck_urls;
            } else if (this.slideDeck.slide_deck_urls && typeof this.slideDeck.slide_deck_urls === 'object') {
                // Convert object with numeric keys to array
                this.slides = Object.keys(this.slideDeck.slide_deck_urls)
                    .sort((a, b) => Number(a) - Number(b))
                    .map(key => this.slideDeck.slide_deck_urls[key])
                    .filter(url => url != null); // Filter out null/undefined values
            } else if (this.slideDeck.slide_deck_urls) {
                // Single value, wrap in array
                this.slides = [this.slideDeck.slide_deck_urls];
            } else {
                this.slides = [];
            }

            // Mark loading as complete once slides are initialized
            this.isLoading = false;

            // Safely access aspect_ratio with null checks
            if (this.slideDeck.aspect_ratio && Array.isArray(this.slideDeck.aspect_ratio)) {
                [this.imageWidth, this.imageHeight] = this.slideDeck.aspect_ratio;
            } else if (this.slideDeck.aspect_ratio && typeof this.slideDeck.aspect_ratio === 'object') {
                // Handle object format with string keys
                this.imageWidth = this.slideDeck.aspect_ratio['0'] || this.slideDeck.aspect_ratio[0];
                this.imageHeight = this.slideDeck.aspect_ratio['1'] || this.slideDeck.aspect_ratio[1];
            }

            // Force re-render after setting dimensions to ensure vueperslides calculates correctly
            if (this.imageWidth && this.imageHeight) {
                this.$nextTick(() => {
                    this.reRender = !this.reRender;
                });
            }

            // Set up thumbnail divisor for multiple slides
            if (this.slides && Array.isArray(this.slides) && this.slides.length > 1) {
                this.thumbnailDivisor = this.slides.length < 8 ? this.slides.length : 8;
            }

            // Only set up keyboard navigation if we have multiple slides
            if (this.slideDeck.total_slides && Number.parseInt(this.slideDeck.total_slides, 10) > 1) {
                // Remove existing listener to prevent duplicates
                document.removeEventListener('keyup', this.changeSlide);
                document.addEventListener('keyup', this.changeSlide);

                if (this.slides && Array.isArray(this.slides)) {
                    // Ensure both sliders are initialized properly once mounted
                    this.$nextTick(() => {
                        if (this.activeSlideIndex > 0) {
                            this.syncSliders(this.activeSlideIndex);
                        }
                    });
                }
            }
        },

        changeSlide(e) {
            e.preventDefault();
            e.stopPropagation();

            if (e.code === 'ArrowRight' || e.code === 'PageDown') {
                if (this.$refs.vueperslides1) {
                    this.$refs.vueperslides1.next();
                }
            } else if (e.code === 'ArrowLeft' || e.code === 'PageUp') {
                if (this.$refs.vueperslides1) {
                    this.$refs.vueperslides1.previous();
                }
            }
        },

        toggle() {
            this.$refs.fullscreen.toggle();
        },

        toggleNavigation() {
            this.fullScreenNavigation = !this.fullScreenNavigation;
            this.calculateWidthHeight();
        },

        fullscreenChange(fullscreen) {
            this.fullscreen = fullscreen;

            if (!this.slides || this.slides.length === 1) return;
            if (!this.$refs.vueperslides1 || !this.$refs.vueperslides2) return;

            const element = this.$refs.vueperslides1.$refs.vueperslides;

            if (this.fullscreen) {
                // NOTE: 1.3 is the margin and border that we set in the css below

                this.thumbnailHeight = this.$refs.vueperslides2.$el.clientHeight * 1.3;

                this.fullScreenNavigation = false;

                this.calculateWidthHeight();
            } else {
                element.style = null;
                this.fullScreenNavigation = true;
                this.$nextTick(() => {
                    this.reRender = !this.reRender;
                });
            }
        },

        calculateWidthHeight() {
            const element = this.$refs.vueperslides1.$refs.vueperslides;

            element.style.width = '100%';

            this.reRender = !this.reRender;
        },

        slideAction(event) {
            this.activeSlideIndex = event.currentSlide.index;

            // Only update the thumbnails if they aren't the source of this change
            if (event.origin !== 'thumbnailSync' && this.$refs.vueperslides2) {
                this.$refs.vueperslides2.goToSlide(this.activeSlideIndex, {
                    emit: false,
                });
            }

            if (this.courseId && this.activityId) {
                this.logEvent(event.currentSlide.index + 1);
            }
        },

        thumbnailAction(event) {
            this.activeSlideIndex = event.currentSlide.index;

            // Only update the main slides if they aren't the source of this change
            if (event.origin !== 'slideSync' && this.$refs.vueperslides1) {
                this.$refs.vueperslides1.goToSlide(this.activeSlideIndex, {
                    emit: false,
                    origin: 'thumbnailSync',
                });
            }

            if (this.courseId && this.activityId) {
                this.logEvent(event.currentSlide.index + 1);
            }
        },

        logEvent(slideNumber) {
            // TODO: Re-enable slide deck event logging after fixing 502 errors
            // Need to implement proper debouncing, request deduplication, and error handling
        },

        syncSliders(index) {
            // Safely synchronize both sliders to the same index
            if (this.$refs.vueperslides1) {
                this.$refs.vueperslides1.goToSlide(index, { emit: false, origin: 'init' });
            }

            if (this.$refs.vueperslides2) {
                this.$refs.vueperslides2.goToSlide(index, { emit: false, origin: 'init' });
            }
        },
    },
});
</script>

<style lang="scss" scoped>
.thumbnails {
    margin: 0.25% auto;

    // Override vueperslides padding-bottom technique to prevent extra space
    // The parallax wrapper uses padding-bottom for aspect ratio, but this creates
    // extra visible space. Instead, let the content define the height.
    :deep(.vueperslides__parallax-wrapper) {
        padding-bottom: 0 !important;
    }

    // Make the track position relative instead of absolute so it takes up space
    :deep(.vueperslides__track) {
        position: relative;
        height: auto;
    }

    // Let slides have natural height based on aspect-ratio CSS
    :deep(.vueperslide) {
        aspect-ratio: 1 / 1;
        height: auto !important;
    }

    &-two-thirds {
        max-width: 66%;
    }

    &-half {
        max-width: 50%;
    }

    &-one-thirds {
        max-width: 33%;
    }
}

.thumbnails .vueperslide {
    box-sizing: border-box;
    border: 1px solid #fff;
    transition: 0.3s ease-in-out;
    opacity: 0.7;
    cursor: pointer;
    background-size: cover;
    background-position: center;
}

.thumbnails .vueperslide--active {
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);
    opacity: 1;
    border-color: #000;
}

.wrapper {
    overflow: hidden !important;
    position: relative !important;
    width: 100%;
}

.fullscreen-icon {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 1;

    &-navigation {
        right: 60px;
    }
}

.slide {
    background-repeat: no-repeat;
    background-position: center;
}

.fullscreen-slide {
    max-height: 100vh;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
}

.button.is-primary.fullscreen-icon-navigation {
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
}

:deep(.vueperslides__arrow) {
    &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: -1;
    }

    &:hover::before {
        background-color: rgba(0, 0, 0, 0.7);
    }

    svg {
        stroke: white;
        stroke-width: 2;
    }
}
</style>
